// src/App.js - Updated with proper contacts handling
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from './components/Navbar';
import Banner from './components/Banner';
import CategoryList from './components/CategoryList';
import ProductList from './components/ProductList';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import Cart from './components/Cart';

const API_BASE_URL = 'https://easymenu-backend-4w2d.onrender.com/api/public';

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/:username" element={<UserWebsite />} />
        <Route path="/" element={<HomePage />} />
      </Routes>
    </Router>
  );
}

function UserWebsite() {
  const { username = 'demo_store' } = useParams();
  const [settings, setSettings] = useState(null);
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('en');
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Detect language from browser
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred_language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    } else {
      const browserLang = navigator.language || navigator.languages?.[0] || 'en';
      if (browserLang.startsWith('km')) {
        setLanguage('km');
        localStorage.setItem('preferred_language', 'km');
      }
    }
  }, []);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('preferred_language', lang);
    setShowSidebar(false);
  };

  useEffect(() => {
    fetchAllData();
  }, [username]);

  useEffect(() => {
    if (selectedCategory !== null || searchTerm) {
      fetchFilteredProducts();
    } else {
      fetchAllProducts();
    }
  }, [selectedCategory, searchTerm]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [settingsRes, bannersRes, categoriesRes, productsRes, contactsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/user/${username}/settings`),
        axios.get(`${API_BASE_URL}/user/${username}/banners`),
        axios.get(`${API_BASE_URL}/user/${username}/categories`),
        axios.get(`${API_BASE_URL}/user/${username}/products`),
        axios.get(`${API_BASE_URL}/user/${username}/contacts`)
      ]);

      setSettings(settingsRes.data);
      setBanners(bannersRes.data);
      setCategories(categoriesRes.data || []);
      setProducts(productsRes.data || []);
      
      // Log contacts to verify
      console.log('Contacts fetched from API:', contactsRes.data);
      setContacts(contactsRes.data || []);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load website data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/user/${username}/products`);
      setProducts(res.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchFilteredProducts = async () => {
    try {
      let url = `${API_BASE_URL}/user/${username}/products`;
      const params = [];
      if (selectedCategory) params.push(`category_id=${selectedCategory}`);
      if (searchTerm) params.push(`search=${searchTerm}`);
      if (params.length) url += `?${params.join('&')}`;
      
      const res = await axios.get(url);
      setProducts(res.data);
    } catch (err) {
      console.error('Error fetching filtered products:', err);
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cart Functions
  const addToCart = (product) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevItems, { 
        ...product, 
        quantity: 1,
        image: product.images && product.images[0] 
          ? `https://easymenu-backend-4w2d.onrender.com${product.images[0]}` 
          : null
      }];
    });
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const handleCheckout = (items, total) => {
    console.log('Checkout:', { items, total });
    // You can add additional checkout logic here
  };

  // Calculate total items in cart
  const getTotalCartItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const translations = {
    en: {
      loading: 'Loading...',
      error: 'Error',
      failedToLoad: 'Failed to load website data',
      noData: 'No data available',
      cart: 'Cart',
      language: 'Language',
      english: 'English',
      khmer: 'ភាសាខ្មែរ',
      close: 'Close'
    },
    km: {
      loading: 'កំពុងផ្ទុក...',
      error: 'កំហុស',
      failedToLoad: 'មិនអាចផ្ទុកទិន្នន័យគេហទំព័រ',
      noData: 'មិនមានទិន្នន័យ',
      cart: 'កន្ត្រក',
      language: 'ភាសា',
      english: 'អង់គ្លេស',
      khmer: 'ភាសាខ្មែរ',
      close: 'បិទ'
    }
  };

  const t = translations[language] || translations.en;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{ backgroundColor: settings?.primary_color || '#f3f4f6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600" style={{ 
            fontFamily: language === 'km' ? "'Kantumruy Pro', 'Noto Sans Khmer', sans-serif" : "'Inter', sans-serif"
          }}>
            {t.loading}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center bg-white p-8 shadow-lg rounded-2xl max-w-md">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-red-500 text-4xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{
            fontFamily: language === 'km' ? "'Kantumruy Pro', 'Noto Sans Khmer', sans-serif" : "'Inter', sans-serif"
          }}>
            {t.error}
          </h2>
          <p className="text-gray-600" style={{
            fontFamily: language === 'km' ? "'Kantumruy Pro', 'Noto Sans Khmer', sans-serif" : "'Inter', sans-serif"
          }}>
            {t.failedToLoad}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {language === 'km' ? 'សាកល្បងម្តងទៀត' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  const styles = {
    primaryColor: settings?.primary_color || '#3B82F6',
    secondaryColor: settings?.secondary_color || '#10B981',
    logoColor: settings?.logo_color || '#000000',
    logoUrl: settings?.logo_url ? `https://easymenu-backend-4w2d.onrender.com${settings.logo_url}` : null
  };

  // Calculate total items for badge
  const totalCartItems = getTotalCartItems();

  // Log contacts for debugging
  console.log('Contacts available in App:', contacts);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
      {/* ══════════ CART COMPONENT ══════════ */}
      <Cart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        primaryColor={styles.primaryColor}
        language={language}
        items={cartItems}
        onUpdateQuantity={updateCartQuantity}
        onRemoveItem={removeFromCart}
        onCheckout={handleCheckout}
        contacts={contacts} // Pass contacts here
        username={username}
      />

      {/* ══════════ FIXED CART BUTTON ══════════ */}
      <div className="fixed bottom-24 right-6 z-40">
        <button
          onClick={() => setIsCartOpen(true)}
          className="group flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-110 active:scale-95 relative"
          style={{ 
            backgroundColor: styles.primaryColor,
            boxShadow: `0 4px 20px ${styles.primaryColor}40`
          }}
          aria-label={t.cart}
        >
          <i className="fas fa-shopping-cart text-white text-xl"></i>
          {totalCartItems > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-white animate-pulse">
              {totalCartItems}
            </span>
          )}
        </button>
      </div>

      {/* ══════════ BACK TO TOP BUTTON ══════════ */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center text-gray-600 hover:text-gray-800 border border-gray-200"
        aria-label="Back to top"
      >
        <i className="fas fa-chevron-up text-sm"></i>
      </button>

      {/* ══════════ LANGUAGE TOGGLE BUTTON ══════════ */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-3">
        <button
          onClick={() => setShowSidebar(!showSidebar)}
          className="w-12 h-12 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center border border-gray-200"
          aria-label={t.language}
        >
          <i className="fas fa-language text-lg text-gray-600"></i>
        </button>
      </div>

      {/* ══════════ LANGUAGE SIDEBAR ══════════ */}
      <>
        {showSidebar && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 z-50 transition-opacity duration-300"
            onClick={() => setShowSidebar(false)}
          />
        )}
        
        <div 
          className={`fixed right-0 top-0 h-full w-72 bg-white shadow-2xl z-50 transition-transform duration-300 ease-in-out ${
            showSidebar ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ fontFamily: language === 'km' ? "'Kantumruy Pro', 'Noto Sans Khmer', sans-serif" : "'Inter', sans-serif" }}
        >
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              <i className="fas fa-language mr-2" style={{ color: styles.primaryColor }}></i>
              {t.language}
            </h3>
            <button
              onClick={() => setShowSidebar(false)}
              className="w-8 h-8 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center"
            >
              <i className="fas fa-times text-gray-500"></i>
            </button>
          </div>

          <div className="p-4 space-y-3">
            <button
              onClick={() => handleLanguageChange('en')}
              className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                language === 'en' 
                  ? 'bg-blue-50 border-2 border-blue-500' 
                  : 'hover:bg-gray-50 border-2 border-transparent'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                language === 'en' ? 'bg-blue-500' : 'bg-gray-200'
              }`}>
                <span className="text-xl">🇬🇧</span>
              </div>
              <div className="flex-1 text-left">
                <p className={`font-medium ${language === 'en' ? 'text-blue-600' : 'text-gray-700'}`}>
                  {t.english}
                </p>
                <p className="text-xs text-gray-400">English</p>
              </div>
              {language === 'en' && (
                <i className="fas fa-check-circle text-blue-500 text-xl"></i>
              )}
            </button>

            <button
              onClick={() => handleLanguageChange('km')}
              className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 ${
                language === 'km' 
                  ? 'bg-blue-50 border-2 border-blue-500' 
                  : 'hover:bg-gray-50 border-2 border-transparent'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                language === 'km' ? 'bg-blue-500' : 'bg-gray-200'
              }`}>
                <span className="text-xl">🇰🇭</span>
              </div>
              <div className="flex-1 text-left">
                <p className={`font-medium ${language === 'km' ? 'text-blue-600' : 'text-gray-700'}`}>
                  {t.khmer}
                </p>
                <p className="text-xs text-gray-400">ភាសាខ្មែរ</p>
              </div>
              {language === 'km' && (
                <i className="fas fa-check-circle text-blue-500 text-xl"></i>
              )}
            </button>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              {language === 'km' ? 'បច្ចុប្បន្ន៖ ភាសាខ្មែរ' : 'Current: English'}
            </p>
          </div>
        </div>
      </>

      <Navbar 
        logoColor={styles.logoColor}
        logoUrl={styles.logoUrl}
        primaryColor={styles.primaryColor}
        secondaryColor={styles.secondaryColor}
        username={username}
        products={products}
        contacts={contacts}
        categories={categories}
        onSearch={handleSearch}
        language={language}
        cartItemsCount={totalCartItems}
        onCartOpen={() => setIsCartOpen(true)}
      />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <CategoryList 
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryClick={handleCategoryClick}
          primaryColor={styles.primaryColor}
          language={language}
        />
        
        <Banner 
          banners={banners} 
          language={language}
        />
        
        <div className="py-8">
          <ProductList 
            products={products}
            primaryColor={styles.primaryColor}
            secondaryColor={styles.secondaryColor}
            contacts={contacts}
            categories={categories}
            searchTerm={searchTerm}
            language={language}
            onAddToCart={addToCart}
          />
        </div>
      </div>
      
      <ContactSection 
        contacts={contacts} 
        primaryColor={styles.primaryColor}
        language={language}
      />
      
      <Footer 
        username={username}
        primaryColor={styles.primaryColor}
        language={language}
      />

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
        @media (max-width: 640px) {
          .fixed.bottom-6.right-6 {
            bottom: 16px;
            right: 16px;
          }
          .fixed.bottom-24.right-6 {
            bottom: 80px;
            right: 16px;
          }
          .fixed.right-6.top-1/2 {
            right: 16px;
          }
          .fixed.right-0.top-0.h-full {
            width: 280px;
          }
        }
      `}</style>
    </div>
  );
}

export default App;
